import { useState } from 'react'
import type { Competitor, CompetitorBrief, BriefScope } from '@ispot/shared'
import { useAIStore } from '@/stores/aiStore'
import { CompetitorLogo } from './CompetitorLogo'
import { InlineBrief } from './InlineBrief'

interface CompetitorBriefRowProps {
  competitor: Competitor
  isExpanded: boolean
  brief: CompetitorBrief | null
  isLoading: boolean
  isFirst: boolean
  onOpen: (id: string) => void
  onClose: () => void
  onGenerate: (scope: BriefScope, apiKey: string) => Promise<void>
  onEnrich?: (id: string, apiKey: string) => Promise<void>
}

const TYPE_LABEL: Record<Competitor['type'], string> = {
  direct: 'Direct',
  adjacent: 'Adjacent',
  potential: 'Potential',
}
const TYPE_COLOR: Record<Competitor['type'], string> = {
  direct: 'var(--color-accent)',
  adjacent: 'var(--color-market-dark)',
  potential: 'var(--color-text-secondary)',
}
const TYPE_BORDER: Record<Competitor['type'], string> = {
  direct: 'rgba(232, 67, 0, 0.28)',
  adjacent: 'rgba(0, 180, 190, 0.32)',
  potential: 'var(--color-border)',
}

const MAX_PILLS = 4

export function CompetitorBriefRow({
  competitor,
  isExpanded,
  brief,
  isLoading,
  isFirst,
  onOpen,
  onClose,
  onGenerate,
  onEnrich,
}: CompetitorBriefRowProps) {
  const apiKey = useAIStore(state => state.apiKey)
  const [rowHover, setRowHover] = useState(false)
  const [btnHover, setBtnHover] = useState(false)

  const positioning = competitor.positioning ?? competitor.whatTheyDo ?? ''
  const allStrengths = competitor.keyCapabilities ?? []
  const allGaps = competitor.notableGaps ?? []
  const strengths = allStrengths.slice(0, MAX_PILLS)
  const gaps = allGaps.slice(0, MAX_PILLS)
  const moreStrengths = allStrengths.length - strengths.length
  const moreGaps = allGaps.length - gaps.length
  const cleanUrl = competitor.url?.replace(/^https?:\/\//, '').replace(/\/$/, '')

  const isEnriching = competitor.enrichmentStatus === 'enriching'

  function handleToggle() {
    if (isExpanded) onClose()
    else onOpen(competitor.id)
  }

  function handleGenerate() {
    if (!apiKey) return
    if (onEnrich) {
      void onEnrich(competitor.id, apiKey)
    } else {
      void onGenerate({ type: 'competitor', competitorId: competitor.id }, apiKey)
    }
  }

  const baseStyle: React.CSSProperties = {
    padding: '22px 0',
    borderTop: isFirst ? '1px solid var(--color-border)' : 'none',
    borderBottom: '1px solid var(--color-border)',
    position: 'relative',
    transition: 'background 180ms var(--ease-standard, ease)',
  }
  const expandedStyle: React.CSSProperties = {
    padding: '24px 28px',
    margin: '18px -28px',
    background: 'var(--color-bg-secondary)',
    borderRadius: '8px',
    borderTop: 'none',
    borderBottom: 'none',
  }
  const hoverBg = rowHover && !isExpanded
    ? 'linear-gradient(to right, transparent 0%, rgba(232,67,0,0.025) 40%, transparent 100%)'
    : 'transparent'

  return (
    <article
      onMouseEnter={() => setRowHover(true)}
      onMouseLeave={() => setRowHover(false)}
      style={{
        ...baseStyle,
        ...(isExpanded ? expandedStyle : {}),
        background: isExpanded ? 'var(--color-bg-secondary)' : hoverBg,
      }}
    >
      {isExpanded && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 28,
            width: 32,
            height: 2,
            background: 'var(--color-accent)',
          }}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: '24px', alignItems: 'start' }}>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', minWidth: 0 }}>
          <CompetitorLogo name={competitor.name} logoUrl={competitor.logoUrl} size={40} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <h4
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  fontSize: '17px',
                  letterSpacing: '-0.012em',
                  color: 'var(--color-text)',
                  margin: 0,
                }}
              >
                {competitor.name}
              </h4>
              {cleanUrl && (
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '12px',
                    color: 'var(--color-text-muted)',
                    letterSpacing: '0.01em',
                  }}
                >
                  {cleanUrl}
                </span>
              )}
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '10px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: TYPE_COLOR[competitor.type],
                  border: `1px solid ${TYPE_BORDER[competitor.type]}`,
                  padding: '2px 6px',
                  borderRadius: '2px',
                  lineHeight: 1.3,
                }}
              >
                {TYPE_LABEL[competitor.type]}
              </span>
            </div>
            {positioning && (
              <p
                style={{
                  margin: '8px 0 0',
                  color: 'var(--color-text-secondary)',
                  fontSize: '14px',
                  lineHeight: 1.55,
                  maxWidth: '62ch',
                }}
              >
                {positioning}
              </p>
            )}
            {(strengths.length > 0 || gaps.length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                {strengths.map((s, i) => (
                  <Pill key={`s-${i}`} kind="strength">{s}</Pill>
                ))}
                {moreStrengths > 0 && <Pill kind="strength-muted">+{moreStrengths} more</Pill>}
                {gaps.map((g, i) => (
                  <Pill key={`g-${i}`} kind="gap">{g}</Pill>
                ))}
                {moreGaps > 0 && <Pill kind="gap-muted">+{moreGaps} more</Pill>}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleToggle}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          aria-expanded={isExpanded}
          style={{
            appearance: 'none',
            font: 'inherit',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            fontSize: '12px',
            color: btnHover ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            padding: '2px 4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
            transition: 'color 180ms var(--ease-standard, ease)',
          }}
        >
          {isExpanded ? 'Close brief' : 'Open brief'}
          <span style={{ fontFamily: 'var(--font-mono)' }}>{isExpanded ? '↓' : '→'}</span>
        </button>
      </div>

      {isExpanded && (
        <InlineBrief
          brief={brief}
          isLoading={isLoading}
          isEnriching={isEnriching}
          canGenerate={Boolean(apiKey)}
          onGenerate={handleGenerate}
        />
      )}
    </article>
  )
}

type PillKind = 'strength' | 'gap' | 'strength-muted' | 'gap-muted'

function Pill({ kind, children }: { kind: PillKind; children: React.ReactNode }) {
  const palette: Record<PillKind, { bg: string; fg: string; dot: string }> = {
    'strength': { bg: 'var(--color-success-bg)', fg: 'var(--color-success-fg)', dot: 'var(--color-success)' },
    'gap': { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-fg)', dot: 'var(--color-warning)' },
    'strength-muted': { bg: 'transparent', fg: 'var(--color-text-muted)', dot: 'var(--color-border-strong)' },
    'gap-muted': { bg: 'transparent', fg: 'var(--color-text-muted)', dot: 'var(--color-border-strong)' },
  }
  const { bg, fg, dot } = palette[kind]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '3px 9px',
        fontSize: '12px',
        lineHeight: 1.2,
        borderRadius: '999px',
        fontWeight: 450,
        background: bg,
        color: fg,
        border: bg === 'transparent' ? '1px solid var(--color-border)' : '1px solid transparent',
      }}
    >
      <span aria-hidden="true" style={{ width: 4, height: 4, borderRadius: '999px', background: dot }} />
      {children}
    </span>
  )
}
